import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, Warehouse, ArrowLeftRight, Plus, RefreshCw, CheckCircle,
  XCircle, AlertTriangle, Boxes, Trash2, History, Eye, Bell
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const Stock = () => {
  const [loading, setLoading] = useState(true);
  const [articulos, setArticulos] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [stockActual, setStockActual] = useState({ lineas: [], valor_total: 0 });
  const [alertas, setAlertas] = useState({ criticas: [], bajas: [], total_alertas: 0 });
  const [activeTab, setActiveTab] = useState("stock");
  
  const [modalArticulo, setModalArticulo] = useState(false);
  const [modalDeposito, setModalDeposito] = useState(false);
  const [modalMovimiento, setModalMovimiento] = useState(false);
  const [modalCategoria, setModalCategoria] = useState(false);
  const [modalKardex, setModalKardex] = useState(false);
  const [kardexData, setKardexData] = useState(null);
  const [loadingKardex, setLoadingKardex] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formArticulo, setFormArticulo] = useState({
    codigo: "", nombre: "", descripcion: "", categoria_id: "",
    unidad_medida_id: "", stock_minimo: 0, stock_maximo: 0, costo_unitario: 0
  });
  const [formDeposito, setFormDeposito] = useState({ codigo: "", nombre: "", direccion: "", responsable: "" });
  const [formCategoria, setFormCategoria] = useState({ codigo: "", nombre: "", descripcion: "" });
  const [formMovimiento, setFormMovimiento] = useState({
    tipo_movimiento_id: "", deposito_origen_id: "", deposito_destino_id: "",
    fecha: new Date().toISOString().split("T")[0], descripcion: "",
    detalles: [{ articulo_id: "", cantidad: 0, costo_unitario: 0 }]
  });

  const fetchArticulos = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/articulos`); setArticulos(r.data); } catch (e) { console.error(e); }
  };
  const fetchDepositos = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/depositos`); setDepositos(r.data); } catch (e) { console.error(e); }
  };
  const fetchCategorias = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/categorias`); setCategorias(r.data); } catch (e) { console.error(e); }
  };
  const fetchUnidades = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/unidades-medida`); setUnidades(r.data); } catch (e) { console.error(e); }
  };
  const fetchTiposMovimiento = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/tipos-movimiento-stock`); setTiposMovimiento(r.data); } catch (e) { console.error(e); }
  };
  const fetchMovimientos = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/movimientos-stock`); setMovimientos(r.data); } catch (e) { console.error(e); }
  };
  const fetchStockActual = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/stock-actual`); setStockActual(r.data); } catch (e) { console.error(e); }
  };
  const fetchAlertas = async () => {
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/alertas-stock`); setAlertas(r.data); } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchArticulos(), fetchDepositos(), fetchCategorias(), fetchUnidades(), 
        fetchTiposMovimiento(), fetchMovimientos(), fetchStockActual(), fetchAlertas()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const verKardex = async (articuloId) => {
    setLoadingKardex(true); setModalKardex(true);
    try { const r = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/kardex/${articuloId}`); setKardexData(r.data); }
    catch (e) { toast.error("Error al cargar kardex"); setModalKardex(false); }
    finally { setLoadingKardex(false); }
  };

  const handleSaveArticulo = async () => {
    if (!formArticulo.codigo || !formArticulo.nombre || !formArticulo.unidad_medida_id) { toast.error("Complete campos requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/articulos`, {
        ...formArticulo, categoria_id: formArticulo.categoria_id ? parseInt(formArticulo.categoria_id) : null,
        unidad_medida_id: parseInt(formArticulo.unidad_medida_id), stock_minimo: parseFloat(formArticulo.stock_minimo) || 0, stock_maximo: parseFloat(formArticulo.stock_maximo) || 0
      });
      toast.success("Artículo creado"); setModalArticulo(false); fetchArticulos(); fetchAlertas();
      setFormArticulo({ codigo: "", nombre: "", descripcion: "", categoria_id: "", unidad_medida_id: "", stock_minimo: 0, stock_maximo: 0, costo_unitario: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const handleSaveDeposito = async () => {
    if (!formDeposito.codigo || !formDeposito.nombre) { toast.error("Complete campos requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/depositos`, formDeposito);
      toast.success("Depósito creado"); setModalDeposito(false); fetchDepositos();
      setFormDeposito({ codigo: "", nombre: "", direccion: "", responsable: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const handleSaveCategoria = async () => {
    if (!formCategoria.codigo || !formCategoria.nombre) { toast.error("Complete campos requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/categorias`, formCategoria);
      toast.success("Categoría creada"); setModalCategoria(false); fetchCategorias();
      setFormCategoria({ codigo: "", nombre: "", descripcion: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const handleSaveMovimiento = async () => {
    const tipo = tiposMovimiento.find(t => t.id === parseInt(formMovimiento.tipo_movimiento_id));
    if (!tipo) { toast.error("Seleccione tipo de movimiento"); return; }
    if (tipo.requiere_origen && !formMovimiento.deposito_origen_id) { toast.error("Requiere depósito origen"); return; }
    if (tipo.requiere_destino && !formMovimiento.deposito_destino_id) { toast.error("Requiere depósito destino"); return; }
    const detallesValidos = formMovimiento.detalles.filter(d => d.articulo_id && parseFloat(d.cantidad) > 0);
    if (detallesValidos.length === 0) { toast.error("Agregue al menos un artículo"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/movimientos-stock`, {
        tipo_movimiento_id: parseInt(formMovimiento.tipo_movimiento_id),
        deposito_origen_id: formMovimiento.deposito_origen_id ? parseInt(formMovimiento.deposito_origen_id) : null,
        deposito_destino_id: formMovimiento.deposito_destino_id ? parseInt(formMovimiento.deposito_destino_id) : null,
        fecha: formMovimiento.fecha, observaciones: formMovimiento.descripcion,
        detalles: detallesValidos.map(d => ({ articulo_id: parseInt(d.articulo_id), cantidad: parseFloat(d.cantidad), costo_unitario: parseFloat(d.costo_unitario) || 0 }))
      });
      toast.success("Movimiento creado"); setModalMovimiento(false); fetchMovimientos(); fetchStockActual(); fetchAlertas();
      setFormMovimiento({ tipo_movimiento_id: "", deposito_origen_id: "", deposito_destino_id: "", fecha: new Date().toISOString().split("T")[0], descripcion: "", detalles: [{ articulo_id: "", cantidad: 0, costo_unitario: 0 }] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const confirmarMovimiento = async (id) => {
    try {
      await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/movimientos-stock/${id}/confirmar?usuario_id=admin`);
      toast.success("Movimiento confirmado"); fetchMovimientos(); fetchStockActual(); fetchAlertas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(v);
  const getEstadoBadge = (e) => ({ OK: <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1"/>OK</Badge>, BAJO: <Badge className="bg-yellow-500"><AlertTriangle className="w-3 h-3 mr-1"/>Bajo</Badge>, CRITICO: <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1"/>Crítico</Badge>, BORRADOR: <Badge variant="outline">Borrador</Badge>, CONFIRMADO: <Badge className="bg-green-500">Confirmado</Badge>, ANULADO: <Badge className="bg-red-500">Anulado</Badge> }[e] || <Badge>{e}</Badge>);
  const addDetalle = () => setFormMovimiento({ ...formMovimiento, detalles: [...formMovimiento.detalles, { articulo_id: "", cantidad: 0, costo_unitario: 0 }] });
  const removeDetalle = (i) => setFormMovimiento({ ...formMovimiento, detalles: formMovimiento.detalles.filter((_, idx) => idx !== i) });
  const updateDetalle = (i, f, v) => { const d = [...formMovimiento.detalles]; d[i][f] = v; setFormMovimiento({ ...formMovimiento, detalles: d }); };
  const calcularTotal = () => formMovimiento.detalles.reduce((s, d) => s + (parseFloat(d.cantidad) || 0) * (parseFloat(d.costo_unitario) || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-7 h-7 text-blue-500" />Gestión de Stock</h1>
          <p className="text-muted-foreground">Control de inventario y movimientos</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchStockActual(); fetchMovimientos(); fetchAlertas(); toast.success("Actualizado"); }}>
          <RefreshCw className="w-4 h-4 mr-2" />Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Artículos</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{articulos.length}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Depósitos</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{depositos.length}</div></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Items en Stock</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stockActual.total_items || 0}</div></CardContent></Card>
        <Card className="bg-blue-50 dark:bg-blue-950"><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Valor Total</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-blue-600">{formatCurrency(stockActual.valor_total || 0)}</div></CardContent></Card>
        {alertas.total_alertas > 0 && (
          <Card className="bg-red-50 dark:bg-red-950 border-red-200 cursor-pointer" onClick={() => setActiveTab("alertas")}>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-red-600 flex items-center gap-1"><Bell className="w-4 h-4" />Alertas</CardTitle></CardHeader>
            <CardContent><div className="text-2xl font-bold text-red-600">{alertas.total_alertas}</div><p className="text-xs text-red-500">{alertas.criticas.length} críticas</p></CardContent>
          </Card>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-xl">
          <TabsTrigger value="stock">Stock</TabsTrigger>
          <TabsTrigger value="articulos">Artículos</TabsTrigger>
          <TabsTrigger value="movimientos">Movimientos</TabsTrigger>
          <TabsTrigger value="depositos">Depósitos</TabsTrigger>
          <TabsTrigger value="alertas" className="relative">{alertas.total_alertas > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />}Alertas</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="space-y-4">
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Boxes className="w-5 h-5" />Stock Actual</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Artículo</TableHead><TableHead>Categoría</TableHead><TableHead className="text-right">Cantidad</TableHead><TableHead className="text-right">Costo Prom.</TableHead><TableHead className="text-right">Valor</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                <TableBody>
                  {stockActual.lineas?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">{item.articulo_codigo}</TableCell>
                      <TableCell>{item.articulo_nombre}</TableCell>
                      <TableCell>{item.categoria || "-"}</TableCell>
                      <TableCell className="text-right">{item.cantidad} {item.unidad}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.costo_promedio)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.valor_stock)}</TableCell>
                      <TableCell>{getEstadoBadge(item.estado)}</TableCell>
                    </TableRow>
                  ))}
                  {(!stockActual.lineas || stockActual.lineas.length === 0) && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No hay stock</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="articulos" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => setModalArticulo(true)}><Plus className="w-4 h-4 mr-2" />Nuevo Artículo</Button>
            <Button variant="outline" onClick={() => setModalCategoria(true)}><Plus className="w-4 h-4 mr-2" />Nueva Categoría</Button>
          </div>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Nombre</TableHead><TableHead>Categoría</TableHead><TableHead>Unidad</TableHead><TableHead className="text-right">Stock Mín.</TableHead><TableHead className="text-right">Stock Actual</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {articulos.map((art) => (
                  <TableRow key={art.id}>
                    <TableCell className="font-mono text-sm">{art.codigo}</TableCell>
                    <TableCell>{art.nombre}</TableCell>
                    <TableCell>{art.categoria_nombre || "-"}</TableCell>
                    <TableCell>{art.unidad_nombre}</TableCell>
                    <TableCell className="text-right">{parseFloat(art.stock_minimo || 0)}</TableCell>
                    <TableCell className="text-right font-medium">{parseFloat(art.stock_total || 0)}</TableCell>
                    <TableCell>{art.activo ? <Badge className="bg-green-500">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => verKardex(art.id)} title="Ver Kardex"><History className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
                {articulos.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No hay artículos</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="movimientos" className="space-y-4">
          <Button onClick={() => setModalMovimiento(true)}><ArrowLeftRight className="w-4 h-4 mr-2" />Nuevo Movimiento</Button>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow><TableHead>Número</TableHead><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Origen</TableHead><TableHead>Destino</TableHead><TableHead className="text-right">Items</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {movimientos.map((mov) => (
                  <TableRow key={mov.id}>
                    <TableCell className="font-mono text-sm">{mov.numero}</TableCell>
                    <TableCell>{new Date(mov.fecha).toLocaleDateString("es-AR")}</TableCell>
                    <TableCell>{mov.tipo_nombre}</TableCell>
                    <TableCell>{mov.deposito_origen_nombre || "-"}</TableCell>
                    <TableCell>{mov.deposito_destino_nombre || "-"}</TableCell>
                    <TableCell className="text-right">{mov.total_items || 0}</TableCell>
                    <TableCell>{getEstadoBadge(mov.estado)}</TableCell>
                    <TableCell>{mov.estado === "BORRADOR" && <Button size="sm" variant="outline" onClick={() => confirmarMovimiento(mov.id)}><CheckCircle className="w-4 h-4 mr-1" />Confirmar</Button>}</TableCell>
                  </TableRow>
                ))}
                {movimientos.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No hay movimientos</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="depositos" className="space-y-4">
          <Button onClick={() => setModalDeposito(true)}><Plus className="w-4 h-4 mr-2" />Nuevo Depósito</Button>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {depositos.map((dep) => (
              <Card key={dep.id}>
                <CardHeader><CardTitle className="flex items-center gap-2"><Warehouse className="w-5 h-5 text-blue-500" />{dep.nombre}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">Código:</span> {dep.codigo}</div>
                    {dep.direccion && <div><span className="text-muted-foreground">Dirección:</span> {dep.direccion}</div>}
                    {dep.responsable && <div><span className="text-muted-foreground">Responsable:</span> {dep.responsable}</div>}
                    <div>{dep.activo ? <Badge className="bg-green-500">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}{dep.es_principal && <Badge className="ml-2 bg-blue-500">Principal</Badge>}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {depositos.length === 0 && <Card className="col-span-full"><CardContent className="text-center text-muted-foreground py-8">No hay depósitos</CardContent></Card>}
          </div>
        </TabsContent>

        <TabsContent value="alertas" className="space-y-4">
          {alertas.total_alertas === 0 ? (
            <Card><CardContent className="text-center py-12"><CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" /><p className="text-lg font-medium">Sin alertas de stock</p><p className="text-muted-foreground">Todos los artículos están en niveles normales</p></CardContent></Card>
          ) : (
            <div className="space-y-4">
              {alertas.criticas.length > 0 && (
                <Card className="border-red-200"><CardHeader><CardTitle className="text-red-600 flex items-center gap-2"><XCircle className="w-5 h-5" />Alertas Críticas ({alertas.criticas.length})</CardTitle></CardHeader>
                  <CardContent>
                    <Table><TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Artículo</TableHead><TableHead>Categoría</TableHead><TableHead className="text-right">Stock Actual</TableHead><TableHead className="text-right">Stock Mín.</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                      <TableBody>{alertas.criticas.map((a, i) => (<TableRow key={i}><TableCell className="font-mono">{a.articulo_codigo}</TableCell><TableCell>{a.articulo_nombre}</TableCell><TableCell>{a.categoria || "-"}</TableCell><TableCell className="text-right font-bold text-red-600">{a.stock_actual} {a.unidad}</TableCell><TableCell className="text-right">{a.stock_minimo}</TableCell><TableCell><Badge className="bg-red-500">{a.mensaje}</Badge></TableCell></TableRow>))}</TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
              {alertas.bajas.length > 0 && (
                <Card className="border-yellow-200"><CardHeader><CardTitle className="text-yellow-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Stock Bajo ({alertas.bajas.length})</CardTitle></CardHeader>
                  <CardContent>
                    <Table><TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Artículo</TableHead><TableHead className="text-right">Stock Actual</TableHead><TableHead className="text-right">Stock Mín.</TableHead><TableHead className="text-right">%</TableHead></TableRow></TableHeader>
                      <TableBody>{alertas.bajas.map((a, i) => (<TableRow key={i}><TableCell className="font-mono">{a.articulo_codigo}</TableCell><TableCell>{a.articulo_nombre}</TableCell><TableCell className="text-right font-bold text-yellow-600">{a.stock_actual} {a.unidad}</TableCell><TableCell className="text-right">{a.stock_minimo}</TableCell><TableCell className="text-right">{a.porcentaje}%</TableCell></TableRow>))}</TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Artículo */}
      <Dialog open={modalArticulo} onOpenChange={setModalArticulo}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo Artículo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Código *</Label><Input value={formArticulo.codigo} onChange={(e) => setFormArticulo({ ...formArticulo, codigo: e.target.value })} placeholder="ART-001" /></div>
              <div className="space-y-2"><Label>Unidad *</Label>
                <Select value={formArticulo.unidad_medida_id} onValueChange={(v) => setFormArticulo({ ...formArticulo, unidad_medida_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{unidades.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.nombre} ({u.abreviatura})</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2"><Label>Nombre *</Label><Input value={formArticulo.nombre} onChange={(e) => setFormArticulo({ ...formArticulo, nombre: e.target.value })} placeholder="Nombre del artículo" /></div>
            <div className="space-y-2"><Label>Categoría</Label>
              <Select value={formArticulo.categoria_id} onValueChange={(v) => setFormArticulo({ ...formArticulo, categoria_id: v })}>
                <SelectTrigger><SelectValue placeholder="Sin categoría" /></SelectTrigger>
                <SelectContent>{categorias.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Stock Mínimo</Label><Input type="number" value={formArticulo.stock_minimo} onChange={(e) => setFormArticulo({ ...formArticulo, stock_minimo: e.target.value })} /></div>
              <div className="space-y-2"><Label>Stock Máximo</Label><Input type="number" value={formArticulo.stock_maximo} onChange={(e) => setFormArticulo({ ...formArticulo, stock_maximo: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalArticulo(false)}>Cancelar</Button><Button onClick={handleSaveArticulo} disabled={saving}>{saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Depósito */}
      <Dialog open={modalDeposito} onOpenChange={setModalDeposito}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo Depósito</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Código *</Label><Input value={formDeposito.codigo} onChange={(e) => setFormDeposito({ ...formDeposito, codigo: e.target.value })} placeholder="DEP-001" /></div>
            <div className="space-y-2"><Label>Nombre *</Label><Input value={formDeposito.nombre} onChange={(e) => setFormDeposito({ ...formDeposito, nombre: e.target.value })} placeholder="Nombre" /></div>
            <div className="space-y-2"><Label>Dirección</Label><Input value={formDeposito.direccion} onChange={(e) => setFormDeposito({ ...formDeposito, direccion: e.target.value })} placeholder="Dirección" /></div>
            <div className="space-y-2"><Label>Responsable</Label><Input value={formDeposito.responsable} onChange={(e) => setFormDeposito({ ...formDeposito, responsable: e.target.value })} placeholder="Responsable" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalDeposito(false)}>Cancelar</Button><Button onClick={handleSaveDeposito} disabled={saving}>{saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Categoría */}
      <Dialog open={modalCategoria} onOpenChange={setModalCategoria}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva Categoría</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Código *</Label><Input value={formCategoria.codigo} onChange={(e) => setFormCategoria({ ...formCategoria, codigo: e.target.value })} placeholder="CAT-001" /></div>
            <div className="space-y-2"><Label>Nombre *</Label><Input value={formCategoria.nombre} onChange={(e) => setFormCategoria({ ...formCategoria, nombre: e.target.value })} placeholder="Nombre" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={formCategoria.descripcion} onChange={(e) => setFormCategoria({ ...formCategoria, descripcion: e.target.value })} placeholder="Descripción" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalCategoria(false)}>Cancelar</Button><Button onClick={handleSaveCategoria} disabled={saving}>{saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Movimiento */}
      <Dialog open={modalMovimiento} onOpenChange={setModalMovimiento}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo Movimiento de Stock</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo *</Label>
                <Select value={formMovimiento.tipo_movimiento_id} onValueChange={(v) => setFormMovimiento({ ...formMovimiento, tipo_movimiento_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{tiposMovimiento.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Fecha *</Label><Input type="date" value={formMovimiento.fecha} onChange={(e) => setFormMovimiento({ ...formMovimiento, fecha: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Depósito Origen</Label>
                <Select value={formMovimiento.deposito_origen_id} onValueChange={(v) => setFormMovimiento({ ...formMovimiento, deposito_origen_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Ninguno" /></SelectTrigger>
                  <SelectContent>{depositos.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Depósito Destino</Label>
                <Select value={formMovimiento.deposito_destino_id} onValueChange={(v) => setFormMovimiento({ ...formMovimiento, deposito_destino_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Ninguno" /></SelectTrigger>
                  <SelectContent>{depositos.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={formMovimiento.descripcion} onChange={(e) => setFormMovimiento({ ...formMovimiento, descripcion: e.target.value })} placeholder="Descripción" /></div>
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3"><Label className="text-base font-semibold">Detalle de Ítems</Label><Button type="button" variant="outline" size="sm" onClick={addDetalle}><Plus className="w-4 h-4 mr-1" />Agregar</Button></div>
              <div className="space-y-3">
                {formMovimiento.detalles.map((det, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-muted/50 rounded-lg">
                    <div className="col-span-5 space-y-1"><Label className="text-xs">Artículo *</Label>
                      <Select value={det.articulo_id} onValueChange={(v) => updateDetalle(i, "articulo_id", v)}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>{articulos.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.codigo} - {a.nombre}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Cantidad *</Label><Input type="number" min="0" step="0.01" value={det.cantidad} onChange={(e) => updateDetalle(i, "cantidad", e.target.value)} /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Costo Unit.</Label><Input type="number" min="0" step="0.01" value={det.costo_unitario} onChange={(e) => updateDetalle(i, "costo_unitario", e.target.value)} /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Subtotal</Label><div className="h-10 flex items-center px-3 bg-background rounded-md border text-sm font-medium">{formatCurrency((parseFloat(det.cantidad) || 0) * (parseFloat(det.costo_unitario) || 0))}</div></div>
                    <div className="col-span-1">{formMovimiento.detalles.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-destructive" onClick={() => removeDetalle(i)}><Trash2 className="w-4 h-4" /></Button>}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg flex justify-between items-center"><span className="font-medium">Total:</span><span className="text-xl font-bold text-blue-600">{formatCurrency(calcularTotal())}</span></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalMovimiento(false)}>Cancelar</Button><Button onClick={handleSaveMovimiento} disabled={saving}>{saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}Guardar ({formMovimiento.detalles.filter(d => d.articulo_id && parseFloat(d.cantidad) > 0).length} ítems)</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Kardex */}
      <Dialog open={modalKardex} onOpenChange={setModalKardex}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" />Kardex - Historial</DialogTitle></DialogHeader>
          {loadingKardex ? <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-blue-500" /></div> : kardexData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div><p className="text-sm text-muted-foreground">Artículo</p><p className="font-semibold">{kardexData.articulo_codigo} - {kardexData.articulo_nombre}</p></div>
                <div><p className="text-sm text-muted-foreground">Período</p><p className="font-semibold">{kardexData.fecha_desde} al {kardexData.fecha_hasta}</p></div>
                <div><p className="text-sm text-muted-foreground">Saldo Final</p><p className="text-xl font-bold text-blue-600">{kardexData.saldo_final} unidades</p></div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Número</TableHead><TableHead className="text-right">Entrada</TableHead><TableHead className="text-right">Salida</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">Costo</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/30"><TableCell colSpan={5} className="font-medium">Saldo Inicial</TableCell><TableCell className="text-right font-medium">{kardexData.saldo_inicial}</TableCell><TableCell colSpan={2}></TableCell></TableRow>
                  {kardexData.movimientos.map((m, i) => (
                    <TableRow key={i}><TableCell>{m.fecha}</TableCell><TableCell>{m.tipo_movimiento}</TableCell><TableCell className="font-mono text-sm">{m.numero}</TableCell>
                      <TableCell className="text-right text-green-600">{m.cantidad_entrada > 0 ? "+" + m.cantidad_entrada : ""}</TableCell>
                      <TableCell className="text-right text-red-600">{m.cantidad_salida > 0 ? "-" + m.cantidad_salida : ""}</TableCell>
                      <TableCell className="text-right font-medium">{m.saldo}</TableCell><TableCell className="text-right">{formatCurrency(m.costo_unitario)}</TableCell><TableCell className="text-right font-medium">{formatCurrency(m.costo_total)}</TableCell>
                    </TableRow>
                  ))}
                  {kardexData.movimientos.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No hay movimientos</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setModalKardex(false)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Stock;
